import React, { Suspense, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { createBrowserRouter, RouteObject, RouterProvider } from 'react-router';

import { e2mTheme } from '@e2m-customer-portal/theme';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';

import * as Highcharts from 'highcharts';
import more from 'highcharts/highcharts-more';
import accessibility from 'highcharts/modules/accessibility';
import exporting from 'highcharts/modules/exporting';
import offline from 'highcharts/modules/offline-exporting';

import { options } from '@e2m-customer-portal/config';
import { EnvSettingsProvider, LoggerProvider, SnackbarProvider } from '@e2m-customer-portal/hooks';
import './index.css';

import { GraphqlClientProvider } from './core/graphql/graphql-client-provider';
import { AuthClientProvider } from './hooks/use-auth-client/use-auth-client';
import { useAuth } from './hooks/use-auth/use-auth';
import { MiddlewareLogger } from './hooks/use-logger/middleware-logger';

import { App } from './App';
import { AppPublic } from './AppPublic';
import * as serviceWorker from './serviceWorker';

import { Loading } from './ui';
import { ErrorComponent } from './ui/error-component/ErrorComponent';

more(Highcharts);
exporting(Highcharts);
offline(Highcharts);
accessibility(Highcharts);
Highcharts.setOptions(options);

type Props = {
  children?: React.ReactNode;
};

const muiCache = createCache({
  key: 'cupo',
  prepend: true,
});

// https://github.com/emotion-js/emotion/issues/1105
muiCache.compat = true;

const ProviderWrapper: React.FC<Props> = ({ children }) => {
  return (
    <React.StrictMode>
      <StyledEngineProvider injectFirst>
        <CacheProvider value={muiCache}>
          <ThemeProvider theme={e2mTheme}>
            <ErrorBoundary FallbackComponent={ErrorComponent}>
              <Suspense fallback={<Loading />}>
                <EnvSettingsProvider>
                  <LoggerProvider logger={new MiddlewareLogger()}>
                    <AuthClientProvider>
                      <GraphqlClientProvider>
                        <SnackbarProvider>{children}</SnackbarProvider>
                      </GraphqlClientProvider>
                    </AuthClientProvider>
                  </LoggerProvider>
                </EnvSettingsProvider>
              </Suspense>
            </ErrorBoundary>
          </ThemeProvider>
        </CacheProvider>
      </StyledEngineProvider>
    </React.StrictMode>
  );
};

export const createRoutes = (isLoggedIn: boolean): RouteObject[] => {
  return [
    {
      path: '*',
      element: isLoggedIn ? <App /> : <AppPublic />,
    },
  ];
};

const AppContainer: React.FC = () => {
  const { isLoggedIn } = useAuth();

  const router = useMemo(() => createBrowserRouter(createRoutes(isLoggedIn)), [isLoggedIn]);
  return <RouterProvider router={router} />;
};

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <ProviderWrapper>
    <AppContainer />
  </ProviderWrapper>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
